<template>
  <Dialog :common-dialog="productDialog" :dialog-width="dialog_width">
    <template v-slot:title>
      <div class="d-flex align-center justify-space-between width-100 pr-2">
        <div class="d-flex align-center">
          <v-avatar color="cyan lighten-5" size="44" rounded class="mr-3">
            <v-icon color="cyan" large>mdi-call-split</v-icon>
          </v-avatar>
          <div>
            <h3 class="font-weight-700 font-size-19 m-0 color-custom-blue">
              Allocate quantity
            </h3>
            <p
              class="font-size-12 font-wight-500 grey--text text--darken-1 m-0"
            >
              Split the ordered quantity across visits and warehouse stock
            </p>
          </div>
        </div>
        <v-btn icon @click="close">
          <v-icon color="grey darken-1">mdi-close</v-icon>
        </v-btn>
      </div>
    </template>

    <template v-slot:body>
      <!-- Product Summary Banner Card -->
      <v-card
        flat
        class="cyan darken-2 white--text rounded-lg pa-3 mb-4 d-flex align-center justify-space-between elevation-0"
      >
        <div class="d-flex align-center">
          <v-avatar
            rounded
            size="44"
            class="mr-3"
            style="background-color: rgba(255, 255, 255, 0.2)"
          >
            <v-icon color="white" large>mdi-package-variant-closed</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-700 font-size-16">
              {{
                itemData.product || "Daikin FTKF71 Compressor Unit 3.0HP"
              }}
            </div>
            <div class="font-size-12 mt-1" style="opacity: 0.85">
              SKU: {{ itemData.barcode || "DKN-CMP-71" }} · ordered quantity
            </div>
          </div>
        </div>
        <div class="text-right pl-3">
          <div class="font-weight-700 font-size-28 line-height-1">
            {{ totalQuantity }}
          </div>
          <div
            class="font-size-10 font-weight-700 text-uppercase mt-1"
            style="opacity: 0.8"
          >
            TOTAL QTY
          </div>
        </div>
      </v-card>

      <!-- Search & Customer Filter Toolbar -->
      <v-card flat class="grey lighten-5 rounded-lg px-3 pt-3 pb-1 mb-4 border">
        <div class="d-flex align-center justify-space-between mb-0">
          <div
            class="d-flex align-center font-weight-700 font-size-12 grey--text text--darken-2 text-uppercase"
            style="letter-spacing: 0.5px"
          >
            <v-icon small color="cyan" class="mr-1">mdi-filter-variant</v-icon>
            Filter Allocation Locations
          </div>
        </div>

        <v-row dense>
          <!-- Search Field (Location / Visit / Tech) -->
          <v-col cols="12" md="12">
            <v-text-field
              v-model="searchQuery"
              placeholder="Search visit, location, tech..."
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              solo
              flat
              hide-details
              clearable
              class="rounded-lg bg-white"
              color="cyan"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card>

      <!-- Destination Allocation Rows -->
      <div class="allocation-destinations mb-4">
        <v-card
          flat
          v-for="(dest, idx) in filteredDestinations"
          :key="dest.id || idx"
          class="border rounded-lg pa-3 mb-3"
          :style="
            isVisitQuantityExceeded(dest)
              ? 'background-color: #fde8e8 !important; border-color: #f87171 !important;'
              : ''
          "
        >
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="d-flex align-center">
              <v-avatar
                size="36"
                rounded
                class="mr-3"
                :color="dest.isWarehouse ? 'amber lighten-4' : 'cyan lighten-5'"
              >
                <v-icon
                  small
                  :color="dest.isWarehouse ? 'amber darken-3' : 'cyan'"
                >
                  {{
                    dest.isWarehouse
                      ? "mdi-package-variant"
                      : "mdi-map-marker-outline"
                  }}
                </v-icon>
              </v-avatar>
              <div>
                <div
                  class="font-weight-700 font-size-14 grey--text text--darken-3"
                >
                  {{ dest.title }}
                </div>
                <div class="font-size-12 grey--text text--darken-1">
                  {{ dest.subtitle }}
                  <span
                    v-if="!dest.isWarehouse && dest.availableQuantity !== undefined"
                    class="font-weight-700 ml-1"
                    :class="
                      isVisitQuantityExceeded(dest)
                        ? 'red--text text--darken-2'
                        : 'grey--text text--darken-2'
                    "
                  >
                    · Product qty: {{ dest.availableQuantity }}
                  </span>
                  <span
                    v-if="dest.customer_name"
                    class="font-weight-700 ml-1 grey--text text--darken-2"
                  >
                    · {{ dest.customer_name }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Quantity Input Field -->
            <div style="width: 80px">
              <v-text-field
                style="min-width: 50px"
                v-model.number="dest.allocated"
                type="number"
                min="0"
                :max="totalQuantity"
                :readonly="dest.isWarehouse"
                dense
                outlined
                solo
                flat
                hide-details
                class="text-center font-weight-700 rounded-lg custom-qty-input"
                color="cyan"
                @input="clampDestination(dest, true, $event)"
                @change="clampDestination(dest, true, $event)"
              ></v-text-field>
            </div>
          </div>

          <!-- Individual Percentage Bar -->
          <div class="d-flex align-center">
            <v-progress-linear
              :value="calcPercentage(dest.allocated)"
              :color="isVisitQuantityExceeded(dest) ? '#c0392b' : 'cyan'"
              background-color="grey lighten-4"
              height="6"
              rounded
              class="flex-grow-1 mr-2"
            ></v-progress-linear>
            <span
              class="font-size-11 font-weight-700 grey--text text--darken-1"
              style="min-width: 32px; text-align: right"
            >
              {{ calcPercentage(dest.allocated) }}%
            </span>
          </div>

          <div
            v-if="isVisitQuantityExceeded(dest)"
            class="pa-2 mt-2 rounded-lg border d-flex align-center"
            style="background-color: #fde8e8; border-color: #f87171 !important"
          >
            <v-icon color="#c0392b" x-small class="mr-1"
              >mdi-alert-outline</v-icon
            >
            <span class="font-size-11 font-weight-700" style="color: #900c3f">
              Allocated {{ dest.allocated }} / product quantity
              {{ dest.availableQuantity }}
            </span>
          </div>
        </v-card>

        <!-- No Results Fallback -->
        <v-card
          flat
          v-if="filteredDestinations.length === 0"
          class="text-center pa-4 grey--text text--darken-1 border rounded-lg"
          style="border-style: dashed !important"
        >
          <v-icon color="grey lighten-1" large class="mb-1"
            >mdi-map-marker-off-outline</v-icon
          >
          <div class="font-size-13 font-weight-medium">
            No locations match the search and customer filter
          </div>
        </v-card>
      </div>

      <!-- Allocation Summary Card -->
      <v-card
        flat
        class="rounded-lg pa-3 mb-3 border"
        :style="
          isOverAllocated
            ? 'background-color: #fde8e8 !important; border-color: #f87171 !important;'
            : ''
        "
      >
        <div
          class="d-flex align-center justify-space-between mb-2 font-size-13"
        >
          <span class="font-weight-700 grey--text text--darken-3">
            Allocated {{ totalAllocated }} of {{ totalQuantity }}
          </span>
          <span
            v-if="isOverAllocated"
            class="font-weight-700"
            style="color: #c0392b"
          >
            Over-allocated -{{ overAllocatedQty }}
          </span>
          <span v-else class="font-weight-700 grey--text text--darken-2">
            Remaining {{ remainingQty }}
          </span>
        </div>
        <v-progress-linear
          :value="Math.min(100, (totalAllocated / (totalQuantity || 1)) * 100)"
          :color="isOverAllocated ? '#c0392b' : 'cyan'"
          background-color="grey lighten-3"
          height="8"
          rounded
        ></v-progress-linear>
      </v-card>

      <!-- Status Banner Alert -->
      <div
        v-if="isOverAllocated"
        class="pa-3 rounded-lg border d-flex align-center"
        style="background-color: #fde8e8; border-color: #f87171 !important"
      >
        <v-icon color="#c0392b" small class="mr-2">mdi-alert-outline</v-icon>
        <span class="font-size-13" style="color: #900c3f">
          <strong class="font-weight-700"
            >Over-allocated by {{ overAllocatedQty }}.</strong
          >
          Reduce the allocation or raise the ordered quantity before saving.
        </span>
      </div>

      <div
        v-else-if="remainingQty > 0"
        class="pa-3 rounded-lg border d-flex align-center"
        style="background-color: #e0f7fa; border-color: #b2ebf2 !important"
      >
        <v-icon color="#00838f" small class="mr-2"
          >mdi-information-outline</v-icon
        >
        <span class="font-size-13" style="color: #006064">
          <strong class="font-weight-700"
            >{{ remainingQty }} unit{{
              remainingQty > 1 ? "s" : ""
            }}
            unallocated.</strong
          >
          Anything left over is booked to Central Warehouse on receipt.
        </span>
      </div>

      <div
        v-else
        class="pa-3 rounded-lg border d-flex align-center"
        style="background-color: #e8f5e9; border-color: #c8e6c9 !important"
      >
        <v-icon color="#2e7d32" small class="mr-2"
          >mdi-check-circle-outline</v-icon
        >
        <span class="font-size-13" style="color: #1b5e20">
          <strong class="font-weight-700">Fully allocated.</strong> Every unit
          is assigned across visits and warehouse stock.
        </span>
      </div>
    </template>

    <template v-slot:action>
      <div class="d-flex align-center justify-space-between width-100 py-2">
        <div class="d-flex align-center">
          <v-btn
            outlined
            small
            color="red"
            class="text-capitalize custom-bold-button rounded-lg"
            @click="clearAll"
          >
            <v-icon left>mdi-refresh</v-icon>
            Clear
          </v-btn>
        </div>

        <div class="d-flex align-center">
          <v-btn
            outlined
            color="grey darken-1"
            class="text-capitalizecustom-grey-border custom-bold-button rounded-lg mr-2"
            @click="close"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="cyan"
            class="white--text text-capitalize custom-bold-button rounded-lg px-4"
            :disabled="hasAllocationError"
            @click="saveAllocation"
          >
            <v-icon small left>mdi-check</v-icon>
            Save allocation
          </v-btn>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script>
import moment from "moment";
import Dialog from "@/view/pages/partials/Dialog.vue";
import { QUERY } from "@/core/services/store/request.module";

export default {
  name: "AllocateQuantityModal",
  components: {
    Dialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    itemData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      searchQuery: "",
      selectedCustomer: null,
      destinations: [],
      companyAddress: "",
      visitsRequestId: 0,
      allocationRequests: Object.create(null),
    };
  },
  computed: {
    dialog_width() {
      return 700;
    },
    productDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    filteredDestinations() {
      let result = this.destinations;

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        result = result.filter(
          (d) =>
            (d.title && d.title.toLowerCase().includes(q)) ||
            (d.subtitle && d.subtitle.toLowerCase().includes(q)) ||
            (d.customer_name && d.customer_name.toLowerCase().includes(q))
        );
      }

      return result;
    },
    totalQuantity() {
      return Number(this.itemData.quantity || this.itemData.qty || 4);
    },
    totalAllocated() {
      return this.destinations.reduce(
        (sum, d) => sum + (Number(d.allocated) || 0),
        0
      );
    },
    remainingQty() {
      return Math.max(0, this.totalQuantity - this.totalAllocated);
    },
    isOverAllocated() {
      return this.totalAllocated > this.totalQuantity;
    },
    visitQuantityExceeded() {
      return this.destinations.some((destination) =>
        this.isVisitQuantityExceeded(destination)
      );
    },
    hasAllocationError() {
      return this.isOverAllocated || this.visitQuantityExceeded;
    },
    overAllocatedQty() {
      return Math.max(0, this.totalAllocated - this.totalQuantity);
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.loadVisitDestinations();
      }
    },
    itemData: {
      handler(val) {
        if (val && val.allocations && val.allocations.length) {
          const allocations = JSON.parse(JSON.stringify(val.allocations));
          const warehouse = allocations.find((allocation) => allocation.isWarehouse);
          this.destinations = [
            ...allocations.filter((allocation) => !allocation.isWarehouse),
            this.warehouseDestination(warehouse),
          ];
        } else if (!this.value) {
          this.destinations = [];
        }
      },
    },
  },
  methods: {
    isVisitQuantityExceeded(destination) {
      if (
        destination.isWarehouse ||
        destination.availableQuantity === undefined ||
        destination.availableQuantity === null
      ) {
        return false;
      }

      return (
        Number(destination.allocated) >
        Number(destination.availableQuantity)
      );
    },
    async loadVisitDestinations() {
      const requestId = ++this.visitsRequestId;
      const quotationId = this.getQuotationId();

      if (!quotationId) {
        this.destinations = [this.warehouseDestination()];
        this.syncWarehouseAllocation();
        return;
      }

      try {
        const productId = Number(this.itemData.product_id || 0);
        const productType = String(this.itemData.product_type || "")
          .toLowerCase()
          .replace("product", "goods");
        const purchaseOrderId = this.getPurchaseOrderId();
        const cacheKey = `${quotationId}:${productId}:${productType}:${purchaseOrderId}`;
        const response = await this.getCachedRequest(
          this.allocationRequests,
          cacheKey,
          {
            url: "allocation/visits",
            data: {
              quotation_id: quotationId,
              product_id: productId,
              product_type: productType || undefined,
              purchase_order_id: purchaseOrderId || undefined,
              current_page: 1,
              // The modal has no page control; the API still supports pagination.
              per_page: 100,
            },
          }
        );

        const visitsById = new Map();
        const rows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
        this.companyAddress =
          response?.data?.company_address ||
          rows[0]?.company_address ||
          this.companyAddress ||
          "";
        rows.forEach((visit) => {
          const visitId = Number(visit.visit_id || visit.id || 0);
          const rawStatus = visit.visit_status || visit.status || 0;
          const status = Number(rawStatus);
          if (
            visitId &&
            status !== 5 &&
            String(rawStatus).toLowerCase() !== "cancelled" &&
            String(rawStatus).toLowerCase() !== "canceled" &&
            !visitsById.has(visitId)
          ) {
            visitsById.set(visitId, { ...visit, id: visitId });
          }
        });

        if (requestId !== this.visitsRequestId) return;

        const existingAllocations = this.getExistingAllocations();

        this.destinations = [
          ...Array.from(visitsById.values()).map((visit) =>
            this.toDestination(visit, existingAllocations[visit.id])
          ),
          this.warehouseDestination(existingAllocations.warehouse),
        ];
        this.clampValues();
      } catch (error) {
        if (requestId === this.visitsRequestId) {
          this.destinations = [this.warehouseDestination()];
        }
      }
    },
    getCachedRequest(cache, key, request) {
      if (!cache[key]) {
        cache[key] = this.$store.dispatch(QUERY, request).catch((error) => {
          delete cache[key];
          throw error;
        });
      }

      return cache[key];
    },
    getQuotationId() {
      return Number(this.itemData.quotation_id || this.itemData.quotation) || 0;
    },
    getPurchaseOrderId() {
      return Number(this.$route?.params?.id || 0) || 0;
    },
    getExistingAllocations() {
      return (this.itemData.allocations || []).reduce(
        (result, allocation) => {
          result[allocation.isWarehouse ? "warehouse" : Number(allocation.id)] =
            allocation;
            
          return result;
        },
        {}
      );
    },
    warehouseDestination(allocation) {
      return {
        ...(allocation || {}),
        id: "warehouse",
        barcode: "WAREHOUSE",
        title: "Warehouse",
        subtitle:
          this.companyAddress ||
          this.itemData.company_address ||
          allocation?.company_address ||
          "",
        customer_name: "Internal Stock",
        allocated: allocation ? Number(allocation.allocated) || 0 : 0,
        isWarehouse: true,
      };
    },
    toDestination(visit, allocation) {
      const barcode = visit.visit_barcode || visit.barcode || `#${visit.id}`;
      const title = visit.visit_title || visit.title || "Visit";
      const date = this.formatVisitDate(
        visit.visit_added_at || visit.added_at || ""
      );
      const engineer = visit.visit_engineer || visit.engineer_name || "";
      let visit_product_quantity =  Number(visit.visit_product_quantity) || 0;
      let purchase_order_quantity =  Number(visit.purchase_order_quantity) || 0;
      let visit_assigned_quantity =  Number(visit.visit_assigned_quantity) || 0;
      let availableQuantity = visit_product_quantity - purchase_order_quantity - visit_assigned_quantity;
      return {
        ...(allocation || {}),
        id: visit.id,
        barcode,
        title: `${barcode} — ${title}`,
        subtitle: [date, engineer].filter(Boolean).join(" · "),
        customer_name:
          visit.customer_company_name || visit.customer_name || visit.customer?.name || "",
        allocated: allocation ? Number(allocation.allocated) || 0 : 0,
        isWarehouse: false,
        availableQuantity: Number(availableQuantity) || 0,
      };
    },
    formatVisitDate(value) {
      if (!value) return "";

      const formatted = moment(value).format("ddd DD MMM");
      return formatted === "Invalid date" ? value : formatted;
    },
    calcPercentage(val) {
      if (!this.totalQuantity) return 0;
      return Math.round(((Number(val) || 0) / this.totalQuantity) * 100);
    },
    clampValues() {
      this.destinations.forEach((d) => {
        this.clampDestination(d, false);
      });
      this.syncWarehouseAllocation();
    },
    clampDestination(destination, syncWarehouse = true, inputValue) {
      const value =
        inputValue !== undefined &&
        inputValue !== null &&
        typeof inputValue !== "object"
          ? inputValue
          : destination.allocated;
      let quantity = Number(
        value
      );
      if (!Number.isFinite(quantity) || quantity < 0) quantity = 0;

      this.$set(destination, "allocated", quantity);
      if (syncWarehouse) this.syncWarehouseAllocation();
    },
    syncWarehouseAllocation() {
      const warehouse = this.destinations.find((destination) =>
        destination.isWarehouse
      );
      if (!warehouse) return;

      const visitAllocated = this.destinations
        .filter((destination) => !destination.isWarehouse)
        .reduce(
          (sum, destination) => sum + (Number(destination.allocated) || 0),
          0
        );

      warehouse.allocated = Math.max(0, this.totalQuantity - visitAllocated);
    },
    clearAll() {
      this.destinations.forEach((d) => {
        d.allocated = 0;
      });
      this.syncWarehouseAllocation();
    },
    close() {
      this.$emit("input", false);
    },
    saveAllocation() {
      // Normalize values again in case a value was pasted or changed before
      // the input event was processed.
      this.clampValues();

      const summaryText = this.destinations
        .filter((d) => d.allocated > 0)
        .map((d) => (d.isWarehouse ? "Whse" : d.barcode || `V00${d.id}`))
        .join(", ");
      const summary = `${summaryText || "Allocated"} · ${this.totalAllocated}/${
        this.totalQuantity
      }`;

      this.$emit("save", {
        summary,
        allocations: this.destinations,
        totalAllocated: this.totalAllocated,
      });
      this.close();
    },
  },
};
</script>

<style scoped>
.text-slate-800 {
  color: #1e293b;
}
.text-slate-700 {
  color: #334155;
}
.text-slate-500 {
  color: #64748b;
}
.text-slate-400 {
  color: #94a3b8;
}
.line-height-1 {
  line-height: 1;
}
.bg-slate-50 {
  background-color: #f8fafc;
}
.bg-white {
  background-color: #ffffff !important;
}
.custom-qty-input >>> input {
  text-align: center !important;
  font-weight: 700 !important;
}
</style>
